import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type {
  ProductionSummary,
  DailyProduction,
  PartSummary,
  RunRecord,
  RunMaterial,
} from "@shared/types";

export function useProductionSummary(from: string, to: string) {
  return useQuery({
    queryKey: ["production", "summary", from, to],
    queryFn: () =>
      apiFetch<ProductionSummary>("/api/production/summary", { from, to }),
  });
}

export function useProductionDaily(
  from: string,
  to: string,
  part?: string
) {
  return useQuery({
    queryKey: ["production", "daily", from, to, part],
    queryFn: () =>
      apiFetch<DailyProduction[]>("/api/production/daily", {
        from,
        to,
        part,
      }),
  });
}

export function useProductionProducts() {
  return useQuery({
    queryKey: ["production", "parts"],
    queryFn: () => apiFetch<PartSummary[]>("/api/production/parts"),
    staleTime: 10 * 60 * 1000,
  });
}

export function useRunMaterials(runId: number | null) {
  return useQuery({
    queryKey: ["production", "run", runId, "materials"],
    queryFn: () =>
      apiFetch<RunMaterial[]>(`/api/production/run/${runId}/materials`),
    enabled: !!runId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProductionRuns(
  from: string,
  to: string,
  part?: string,
  page = 1,
  limit = 25
) {
  return useQuery({
    queryKey: ["production", "runs", from, to, part, page, limit],
    queryFn: () =>
      apiFetch<{ data: RunRecord[]; total: number; page: number; limit: number }>(
        "/api/production/runs",
        { from, to, part, page, limit }
      ),
  });
}
